import { UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import { App, Button, Checkbox, Col, Drawer, Flex, Form, Input, InputNumber, Row, Select, Upload } from 'antd';
import { get, isNull, omitBy } from 'lodash';
import mime from 'mime';
import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * APIs
 */
import { attachmentsPresigned, cAttachment, uAttachment } from '@/apis/attachment';

/**
 * Types
 */
import type { AttachmentDto, PresignedResDto } from '@/dtos/attachment.dto';
import { getAudioMeta, getImageMeta, getVideoMeta } from '@/utils';
import type { DrawerProps, FormProps, UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';

export type Action =
    | {
          update: (data: AttachmentDto) => void;
      }
    | undefined;

type Props = {
    onFinish?: (data: AttachmentDto) => void;
};

type State = {
    open: boolean;
    type: 'create' | 'update';
};

const ComponentCreateUpdate = forwardRef<Action, Props>((props, ref) => {
    /**
     * Hooks
     */
    const { message } = App.useApp();

    const [form] = Form.useForm();

    const { t } = useTranslation();

    /**
     * States
     */
    const [state, setState] = useSetState<State>({
        open: false,
        type: 'create',
    });

    /**
     * Requests
     */
    const { isPending: pendingCAttachment, mutate: mutateCAttachment } = useMutation({
        mutationKey: ['cAttachment'],
        mutationFn: cAttachment,
        onSuccess: ({ data }) => {
            // 关闭抽屉
            setState({
                open: false,
            });

            // 执行回调
            props?.onFinish?.(data);
        },
    });

    const { isPending: pendingUAttachment, mutate: mutateUAttachment } = useMutation({
        mutationKey: ['uAttachment'],
        mutationFn: uAttachment,
        onSuccess: ({ data }) => {
            // 关闭抽屉
            setState({
                open: false,
            });

            // 执行回调
            props?.onFinish?.(data);
        },
    });

    /**
     * ChildrenProps
     */
    const drawerProps: DrawerProps = {
        closeIcon: false,
        extra: (
            <Flex gap={8}>
                <Button
                    onClick={() => {
                        setState({
                            open: false,
                        });
                    }}
                >
                    {t('cancel')}
                </Button>

                <Button loading={pendingCAttachment || pendingUAttachment} type="primary" onClick={form.submit}>
                    {t('save')}
                </Button>
            </Flex>
        ),
        maskClosable: false,
        open: state.open,
        size: 'large',
        title: state.type === 'create' ? t('upload') : t('edit'),
        afterOpenChange: (open) => {
            if (open) return;

            form.resetFields();
        },
        onClose: () => {
            setState({
                open: false,
            });
        },
    };

    const formProps: FormProps = {
        autoComplete: 'off',
        form,
        initialValues: {
            app: 'dashboard',
            module: 'attachment',
            prefix: 'default',
            type: 'unknown',
        },
        layout: 'vertical',
        onFinish: (values) => {
            values.path = get(values, 'path[0].data.path');
            values.prefix = values.prefix === 'default' ? undefined : values.prefix;

            if (state.type === 'create') {
                return mutateCAttachment(values);
            }

            if (state.type === 'update') {
                return mutateUAttachment(values);
            }
        },
        onValuesChange: (values) => {
            if (values.extension) {
                form.setFieldsValue({
                    mime_type: mime.getType(values.extension) || undefined,
                });
            }
        },
    };

    const uploadProps: UploadProps = {
        maxCount: 1,
        beforeUpload: async (file: RcFile & { data?: PresignedResDto }) => {
            // 生成预签名 URL
            const { code, data } = await attachmentsPresigned({
                extension: mime.getExtension(file.type) || file.name.split('.').pop() || 'unknown',
                options: {
                    Method: 'PUT',
                },
            });

            file.data = data;

            return code === 0;
        },
        customRequest: (options) => {
            const file = options.file as RcFile & { data: PresignedResDto };

            fetch(file.data.url, {
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
                method: 'PUT',
            })
                .then(async (res) => {
                    if (res.status !== 200) {
                        return message.error(res.statusText);
                    }

                    const fields: Record<string, any> = {
                        duration: undefined,
                        extension: mime.getExtension(file.type) || file.name.split('.').pop() || 'unknown',
                        height: undefined,
                        mime_type: file.type,
                        name: file.name,
                        path: file.data.path.replace('temp/', ''),
                        size: file.size,
                        width: undefined,
                    };

                    if (file.type.startsWith('audio/')) {
                        const audioMeta = await getAudioMeta(file);

                        fields.duration = audioMeta.duration;
                        fields.type = 'audio';
                    }

                    if (file.type.startsWith('image/')) {
                        const imageMeta = await getImageMeta(file);

                        fields.height = imageMeta.height;
                        fields.width = imageMeta.width;
                        fields.type = 'image';
                    }

                    if (file.type.startsWith('video/')) {
                        const videoMeta = await getVideoMeta(file);

                        fields.duration = videoMeta.duration;
                        fields.height = videoMeta.height;
                        fields.width = videoMeta.width;
                        fields.type = 'video';
                    }

                    form.setFieldsValue(fields);

                    options.onSuccess?.(res);
                })
                .catch(options.onError);
        },
    };

    /**
     * Handles
     */
    useImperativeHandle(ref, () => {
        return {
            update: (data) => {
                form.setFieldsValue({
                    ...omitBy(data, isNull),
                    path: [
                        {
                            data: {
                                path: data.path,
                            },
                            name: data.name || t('unnamed'),
                        },
                    ],
                });

                setState({
                    open: true,
                    type: 'update',
                });
            },
        };
    });

    return (
        <>
            <Button
                type="primary"
                onClick={() => {
                    setState({
                        open: true,
                        type: 'create',
                    });
                }}
            >
                {t('upload')}
            </Button>

            <Drawer {...drawerProps}>
                <Form {...formProps}>
                    <Form.Item hidden name="id">
                        <Input />
                    </Form.Item>

                    <Form.Item hidden name="version">
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label={t('upload')}
                        name="path"
                        rules={[{ required: true }]}
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                    >
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />}>{t('upload')}</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item label={t('name')} name="name">
                        <Input />
                    </Form.Item>

                    <Row gutter={24}>
                        <Col span={8}>
                            <Form.Item label={t('page.attachments.app')} name="app" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t('page.attachments.extension')}
                                name="extension"
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label={t('page.attachments.module')} name="module" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t('page.attachments.mime_type')}
                                name="mime_type"
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label={t('page.attachments.prefix')} name="prefix">
                                <Select
                                    options={[
                                        {
                                            label: t('default'),
                                            value: 'default',
                                        },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item label={t('page.attachments.type')} name="type" rules={[{ required: true }]}>
                                <Select
                                    options={[
                                        {
                                            label: t('page.attachments.audio'),
                                            value: 'audio',
                                        },
                                        {
                                            label: t('page.attachments.image'),
                                            value: 'image',
                                        },
                                        {
                                            label: t('page.attachments.unknown'),
                                            value: 'unknown',
                                        },
                                        {
                                            label: t('page.attachments.video'),
                                            value: 'video',
                                        },
                                        {
                                            label: t('page.attachments.zip'),
                                            value: 'zip',
                                        },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={6}>
                            <Form.Item label={t('page.attachments.width')} name="width">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label={t('page.attachments.height')} name="height">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label={t('page.attachments.duration')} name="duration">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label={t('page.attachments.size')} name="size">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label={t('status')}>
                        <Row gutter={[8, 8]}>
                            <Col span={12}>
                                <Form.Item name="is_violation" noStyle={true} valuePropName="checked">
                                    <Checkbox>{t('status_violation')}</Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
});

export default ComponentCreateUpdate;
